import React, { useState, useEffect, useMemo } from 'react';

const F = 'system-ui,-apple-system,"Helvetica Neue",Arial,sans-serif';
const B = {
  teal: '#314F5C', mid: '#6F8FA7', pale: '#E0F5FC', bg: '#f2f1ef',
  card: '#ffffff', text: '#163040', sub: '#6F8FA7', border: '#e9eef2',
  green: '#2DA06A', red: '#c0504a', amber: '#b89a4a', purple: '#5e2e72',
};

const CATEGORIES = ['all', 'general', 'tourist', 'work', 'student', 'residency', 'transit'];
const CAT_COLORS = {
  general:   { bg: 'rgba(49,79,92,0.06)', color: B.mid },
  tourist:   { bg: 'rgba(45,160,106,0.08)', color: B.green },
  work:      { bg: 'rgba(184,154,74,0.08)', color: B.amber },
  student:   { bg: 'rgba(49,79,92,0.06)', color: B.teal },
  residency: { bg: 'rgba(94,46,114,0.08)', color: B.purple },
  transit:   { bg: 'rgba(49,79,92,0.06)', color: B.mid },
};

function useIsMobile() {
  const [mob, setMob] = React.useState(() => typeof window !== 'undefined' && window.innerWidth < 768);
  React.useEffect(() => {
    const h = () => setMob(window.innerWidth < 768);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);
  return mob;
}

export default function VisaPortals({ session }) {
  const isMobile = useIsMobile();
  const [portals, setPortals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [catFilter, setCatFilter] = useState('all');
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(null);

  const SB_URL = 'https://dekxnqbjzgkvltclthqw.supabase.co';
  const SB_KEY = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImRla3hucWJqemdrdmx0Y2x0aHF3Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3NjE3NzM1ODEsImV4cCI6MjA3NzM0OTU4MX0.07Aj6pv1hhGAIzMgdkoseoSRJscx3vH3Uy9Bq472ubM';

  const sbFetch = async (path, opts = {}) => {
    const res = await fetch(`${SB_URL}/rest/v1/${path}`, {
      ...opts,
      headers: {
        apikey: SB_KEY,
        Authorization: `Bearer ${session?.access_token || SB_KEY}`,
        'Content-Type': 'application/json',
        Prefer: opts.method === 'POST' ? 'return=representation' : (opts.method === 'PATCH' ? 'return=representation' : undefined),
        ...opts.headers,
      },
    });
    if (!res.ok) throw new Error(await res.text());
    const text = await res.text();
    return text ? JSON.parse(text) : null;
  };

  const loadPortals = async () => {
    setLoading(true);
    try {
      const data = await sbFetch('visa_portals?order=country,category');
      setPortals(data || []);
    } catch (e) { console.error('Load portals error:', e); }
    setLoading(false);
  };

  useEffect(() => { loadPortals(); }, []); // eslint-disable-line

  const countries = useMemo(() => {
    const set = new Set(portals.map(p => p.country));
    return [...set].sort();
  }, [portals]);

  const filtered = useMemo(() => {
    let list = portals;
    if (catFilter !== 'all') list = list.filter(p => p.category === catFilter);
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(p =>
        p.country.toLowerCase().includes(q) ||
        p.portal_name.toLowerCase().includes(q) ||
        (p.visa_type || '').toLowerCase().includes(q) ||
        (p.notes || '').toLowerCase().includes(q)
      );
    }
    return list;
  }, [portals, catFilter, search]);

  const grouped = useMemo(() => {
    const map = {};
    filtered.forEach(p => {
      if (!map[p.country]) map[p.country] = [];
      map[p.country].push(p);
    });
    return Object.entries(map).sort((a, b) => a[0].localeCompare(b[0]));
  }, [filtered]);

  const stats = useMemo(() => ({
    total: portals.length,
    countries: new Set(portals.map(p => p.country)).size,
    tourist: portals.filter(p => p.category === 'tourist').length,
    work: portals.filter(p => p.category === 'work').length,
    student: portals.filter(p => p.category === 'student').length,
    residency: portals.filter(p => p.category === 'residency').length,
  }), [portals]);

  const savePortal = async (formData) => {
    setSaving(true);
    try {
      if (editing) {
        await sbFetch(`visa_portals?id=eq.${editing.id}`, {
          method: 'PATCH',
          body: JSON.stringify(formData),
        });
      } else {
        await sbFetch('visa_portals', {
          method: 'POST',
          body: JSON.stringify(formData),
        });
      }
      setEditing(null);
      setShowForm(false);
      await loadPortals();
    } catch (e) { alert('Save failed: ' + e.message); }
    setSaving(false);
  };

  const deletePortal = async (id) => {
    if (!window.confirm('Delete this portal link?')) return;
    setDeleting(id);
    try {
      await sbFetch(`visa_portals?id=eq.${id}`, { method: 'DELETE' });
      await loadPortals();
    } catch (e) { alert('Delete failed: ' + e.message); }
    setDeleting(null);
  };

  // ── Styles ───────────────────────────────────────────────────────────────
  const S = {
    page: { padding: isMobile ? '16px 14px 80px' : '24px 28px', fontFamily: F, color: B.text },
    header: { display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20, flexWrap: 'wrap' },
    title: { fontSize: 22, fontWeight: 700, letterSpacing: '-0.3px', margin: 0, color: B.text },
    sub: { fontSize: 13, color: B.sub, marginTop: 2 },
    addBtn: {
      padding: '8px 16px', borderRadius: 8, border: 'none', cursor: 'pointer',
      background: B.teal, color: '#fff', fontSize: 13, fontWeight: 600, fontFamily: F,
    },
    statsRow: {
      display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2,1fr)' : 'repeat(6,1fr)',
      gap: 10, marginBottom: 20,
    },
    stat: {
      background: B.card, borderRadius: 10, padding: '14px 16px', textAlign: 'center',
      boxShadow: '0 1px 3px rgba(15,30,40,0.07), 0 0 0 1px rgba(15,30,40,0.04)',
    },
    statVal: { fontSize: 22, fontWeight: 700, color: B.teal },
    statLabel: { fontSize: 11, color: B.sub, marginTop: 2, textTransform: 'uppercase', letterSpacing: 0.5 },
    controls: {
      display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center',
    },
    search: {
      flex: 1, minWidth: 180, padding: '8px 12px', borderRadius: 8, border: '1px solid #dde8ed',
      fontSize: 13, fontFamily: F, outline: 'none', background: '#fafcfd',
    },
    pills: { display: 'flex', gap: 4, flexWrap: 'wrap' },
    pill: (active) => ({
      padding: '5px 12px', borderRadius: 20, border: 'none', cursor: 'pointer',
      fontSize: 12, fontWeight: 600, fontFamily: F,
      background: active ? B.teal : 'rgba(49,79,92,0.06)',
      color: active ? '#fff' : B.sub,
    }),
    countrySection: { marginBottom: 20 },
    countryTitle: {
      fontSize: 15, fontWeight: 700, color: B.teal, marginBottom: 8,
      display: 'flex', alignItems: 'center', gap: 8,
    },
    countryCount: {
      fontSize: 11, background: 'rgba(49,79,92,0.06)', color: B.sub,
      padding: '2px 8px', borderRadius: 10, fontWeight: 600,
    },
    table: {
      width: '100%', borderCollapse: 'separate', borderSpacing: 0,
      background: B.card, borderRadius: 12, overflow: 'hidden',
      boxShadow: '0 1px 3px rgba(15,30,40,0.07), 0 0 0 1px rgba(15,30,40,0.04)',
    },
    th: {
      textAlign: 'left', padding: '10px 14px', fontSize: 11, fontWeight: 600,
      color: B.sub, textTransform: 'uppercase', letterSpacing: 0.5,
      borderBottom: '1px solid ' + B.border, background: '#fafcfd',
    },
    td: {
      padding: '10px 14px', fontSize: 13, color: B.text, borderBottom: '1px solid ' + B.border,
      verticalAlign: 'top',
    },
    badge: (cat) => {
      const c = CAT_COLORS[cat] || CAT_COLORS.general;
      return {
        display: 'inline-block', padding: '2px 8px', borderRadius: 6,
        fontSize: 11, fontWeight: 600, background: c.bg, color: c.color,
      };
    },
    link: { color: B.teal, textDecoration: 'none', fontSize: 12, wordBreak: 'break-all' },
    actions: { display: 'flex', gap: 6 },
    actionBtn: (color) => ({
      padding: '4px 10px', borderRadius: 6, border: 'none', cursor: 'pointer',
      fontSize: 11, fontWeight: 600, fontFamily: F, background: color === 'danger' ? 'rgba(192,80,74,0.08)' : 'rgba(49,79,92,0.06)',
      color: color === 'danger' ? B.red : B.teal,
    }),
    overlay: {
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 9999,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    },
    modal: {
      background: '#fff', borderRadius: 14, padding: '24px 28px', width: isMobile ? '92%' : 480,
      maxHeight: '85vh', overflowY: 'auto',
      boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
    },
    modalTitle: { fontSize: 18, fontWeight: 700, color: B.text, marginBottom: 16 },
    field: { marginBottom: 12 },
    label: { fontSize: 12, fontWeight: 600, color: B.sub, marginBottom: 4, display: 'block' },
    input: {
      width: '100%', padding: '8px 12px', borderRadius: 8, border: '1px solid #dde8ed',
      fontSize: 13, fontFamily: F, outline: 'none', boxSizing: 'border-box',
    },
    select: {
      width: '100%', padding: '8px 12px', borderRadius: 8, border: '1px solid #dde8ed',
      fontSize: 13, fontFamily: F, outline: 'none', background: '#fff', boxSizing: 'border-box',
    },
    textarea: {
      width: '100%', padding: '8px 12px', borderRadius: 8, border: '1px solid #dde8ed',
      fontSize: 13, fontFamily: F, outline: 'none', minHeight: 60, resize: 'vertical', boxSizing: 'border-box',
    },
    modalFooter: { display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 },
    cancelBtn: {
      padding: '8px 16px', borderRadius: 8, border: '1px solid #dde8ed', cursor: 'pointer',
      background: '#fff', color: B.sub, fontSize: 13, fontWeight: 600, fontFamily: F,
    },
    saveBtn: {
      padding: '8px 20px', borderRadius: 8, border: 'none', cursor: 'pointer',
      background: B.teal, color: '#fff', fontSize: 13, fontWeight: 600, fontFamily: F,
      opacity: saving ? 0.6 : 1,
    },
    empty: { textAlign: 'center', padding: 40, color: B.sub, fontSize: 14 },
  };

  return (
    <div style={S.page}>
      {/* Header */}
      <div style={S.header}>
        <div style={{ flex: 1 }}>
          <h1 style={S.title}>Visa Portals</h1>
          <div style={S.sub}>Government visa and immigration portals — {stats.countries} countries, {stats.total} links</div>
        </div>
        <button style={S.addBtn} onClick={() => { setEditing(null); setShowForm(true); }}>+ Add Portal</button>
      </div>

      {/* Stats */}
      <div style={S.statsRow}>
        <div style={S.stat}><div style={S.statVal}>{stats.total}</div><div style={S.statLabel}>Total Links</div></div>
        <div style={S.stat}><div style={S.statVal}>{stats.countries}</div><div style={S.statLabel}>Countries</div></div>
        <div style={S.stat}><div style={{...S.statVal, color: B.green}}>{stats.tourist}</div><div style={S.statLabel}>Tourist</div></div>
        <div style={S.stat}><div style={{...S.statVal, color: B.amber}}>{stats.work}</div><div style={S.statLabel}>Work</div></div>
        <div style={S.stat}><div style={{...S.statVal, color: B.teal}}>{stats.student}</div><div style={S.statLabel}>Student</div></div>
        <div style={S.stat}><div style={{...S.statVal, color: B.purple}}>{stats.residency}</div><div style={S.statLabel}>Residency</div></div>
      </div>

      {/* Filter controls */}
      <div style={S.controls}>
        <input
          style={S.search}
          placeholder="Search by country, portal name, visa type..."
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <div style={S.pills}>
          {CATEGORIES.map(cat => (
            <button key={cat} style={S.pill(catFilter === cat)} onClick={() => setCatFilter(cat)}>
              {cat === 'all' ? 'All' : cat.charAt(0).toUpperCase() + cat.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {/* Loading */}
      {loading && <div style={S.empty}>Loading portals...</div>}

      {/* Grouped by country */}
      {!loading && grouped.length === 0 && <div style={S.empty}>No portals match your filters.</div>}
      {!loading && grouped.map(([country, items]) => (
        <div key={country} style={S.countrySection}>
          <div style={S.countryTitle}>
            {country}
            <span style={S.countryCount}>{items.length}</span>
          </div>
          <table style={S.table}>
            <thead>
              <tr>
                <th style={S.th}>Portal Name</th>
                <th style={S.th}>Category</th>
                <th style={S.th}>Visa Type</th>
                <th style={{...S.th, ...(isMobile ? {display:'none'} : {})}}>URL</th>
                <th style={{...S.th, ...(isMobile ? {display:'none'} : {})}}>Notes</th>
                <th style={{...S.th, width: 100}}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map(p => (
                <tr key={p.id}>
                  <td style={S.td}><strong>{p.portal_name}</strong></td>
                  <td style={S.td}><span style={S.badge(p.category)}>{p.category}</span></td>
                  <td style={S.td}>{p.visa_type || '—'}</td>
                  <td style={{...S.td, ...(isMobile ? {display:'none'} : {})}}><a href={p.url} target="_blank" rel="noopener noreferrer" style={S.link}>{p.url}</a></td>
                  <td style={{...S.td, ...(isMobile ? {display:'none'} : {}), fontSize: 12, color: B.sub}}>{p.notes || '—'}</td>
                  <td style={S.td}>
                    <div style={S.actions}>
                      <button style={S.actionBtn('edit')} onClick={() => { setEditing(p); setShowForm(true); }}>Edit</button>
                      <button style={S.actionBtn('danger')} disabled={deleting === p.id} onClick={() => deletePortal(p.id)}>
                        {deleting === p.id ? '...' : 'Del'}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}

      {/* Add/Edit Modal */}
      {showForm && (
        <PortalForm
          portal={editing}
          countries={countries}
          saving={saving}
          onSave={savePortal}
          onClose={() => { setShowForm(false); setEditing(null); }}
          S={S}
        />
      )}
    </div>
  );
}

function PortalForm({ portal, countries, saving, onSave, onClose, S }) {
  const [form, setForm] = useState({
    country: portal?.country || '',
    portal_name: portal?.portal_name || '',
    url: portal?.url || '',
    category: portal?.category || 'general',
    visa_type: portal?.visa_type || '',
    notes: portal?.notes || '',
  });

  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const handleSave = () => {
    if (!form.country.trim() || !form.portal_name.trim() || !form.url.trim()) {
      alert('Country, portal name, and URL are required.');
      return;
    }
    onSave(form);
  };

  return (
    <div style={S.overlay} onClick={onClose}>
      <div style={S.modal} onClick={e => e.stopPropagation()}>
        <div style={S.modalTitle}>{portal ? 'Edit Portal' : 'Add Portal'}</div>

        <div style={S.field}>
          <label style={S.label}>Country</label>
          <input
            style={S.input}
            list="country-list"
            value={form.country}
            onChange={e => set('country', e.target.value)}
            placeholder="e.g. United Kingdom"
          />
          <datalist id="country-list">
            {countries.map(c => <option key={c} value={c} />)}
          </datalist>
        </div>

        <div style={S.field}>
          <label style={S.label}>Portal Name</label>
          <input style={S.input} value={form.portal_name} onChange={e => set('portal_name', e.target.value)} placeholder="e.g. UK Visas & Immigration" />
        </div>

        <div style={S.field}>
          <label style={S.label}>URL</label>
          <input style={S.input} type="url" value={form.url} onChange={e => set('url', e.target.value)} placeholder="https://..." />
        </div>

        <div style={S.field}>
          <label style={S.label}>Category</label>
          <select style={S.select} value={form.category} onChange={e => set('category', e.target.value)}>
            <option value="general">General</option>
            <option value="tourist">Tourist</option>
            <option value="work">Work</option>
            <option value="student">Student</option>
            <option value="residency">Residency</option>
            <option value="transit">Transit</option>
          </select>
        </div>

        <div style={S.field}>
          <label style={S.label}>Visa Type</label>
          <input style={S.input} value={form.visa_type} onChange={e => set('visa_type', e.target.value)} placeholder="e.g. Skilled Worker, e-Visa, Golden Visa" />
        </div>

        <div style={S.field}>
          <label style={S.label}>Notes</label>
          <textarea style={S.textarea} value={form.notes} onChange={e => set('notes', e.target.value)} placeholder="Additional details..." />
        </div>

        <div style={S.modalFooter}>
          <button style={S.cancelBtn} onClick={onClose}>Cancel</button>
          <button style={S.saveBtn} disabled={saving} onClick={handleSave}>
            {saving ? 'Saving...' : (portal ? 'Update' : 'Add Portal')}
          </button>
        </div>
      </div>
    </div>
  );
}
